import { useState, useEffect } from 'react';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import MessageBox from '../../MessageBox/MessageBox';
import { IAttachmentDB } from '../BandPage/BandPage';
import './AttachmentSegment.css';
import { isAxiosError } from 'axios';

interface props {
  data: IAttachmentDB;
}

const EMPTY_FILE_BOX_CONTENT = 'Naciśnij tu, aby wybrać plik';
const SETTING_HOVER_TEXT = "Kliknij tutaj aby dodać lub wymienić ustawienie";
const PICTURE_HOVER_TEXT = "Kliknij tutaj aby dodać lub wymienić zdjęcie";

const AttachmentSegment = (props: props) => {
  const axiosPrivate = useAxiosPrivate();
  const bandId = sessionStorage.getItem('editedBandId');
  const isAdmin = sessionStorage.getItem('roles') === 'admin';

  // URL-e do odczytu (GET) - bez prefiksu admin
  const SETTING_URL = bandId ? `/images/setting/${bandId}` : '/images/setting';
  const BAND_URL = bandId ? `/images/band/${bandId}` : '/images/band';

  // URL-e do zapisu (POST) - z prefiksem admin dla administratora
  const SETTING_UPLOAD_URL = bandId ? 
    (isAdmin ? `/admin/images/setting/${bandId}` : `/images/setting/${bandId}`) : 
    '/images/setting';
  const BAND_UPLOAD_URL = bandId ? 
    (isAdmin ? `/admin/images/band/${bandId}` : `/images/band/${bandId}`) : 
    '/images/band';

  const DEFAULT_STAGE_IMAGE = process.env.PUBLIC_URL + '/images/scena.png';
  const DEFAULT_BAND_IMAGE = process.env.PUBLIC_URL + '/images/zdjecie_domyslne.png';

  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(false);
  const [showUploadMessage, setShowUploadMessage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null); 
  const [settingPreview, setSettingPreview] = useState<string | null>(null);
  const [positionFile, setPositionFile] = useState<File | null>(null);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        console.log('Próba pobrania zdjęcia zespołu z:', BAND_URL);
        const response = await axiosPrivate.get(BAND_URL, {
          responseType: 'blob',
          headers: {
            'Accept': 'image/*'
          }
        });
        
        if (response.data.size === 0) {
          console.log('Ustawianie domyślnego zdjęcia zespołu:', DEFAULT_BAND_IMAGE);
          setImagePreview(DEFAULT_BAND_IMAGE);
          return;
        }

        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const imageUrl = URL.createObjectURL(blob);
        setImagePreview(imageUrl);
      } catch (error) {
        console.error('Błąd podczas pobierania zdjęcia zespołu:', error);
        if (isAxiosError(error) && error.response) {
          console.error('Status błędu:', error.response.status);
          console.log('Ustawianie domyślnego zdjęcia zespołu po błędzie:', DEFAULT_BAND_IMAGE);
          setImagePreview(DEFAULT_BAND_IMAGE);
        }
      }
    };

    fetchImage();

    return () => {
      if (imagePreview && !imagePreview.includes('zdjecie_domyslne.png')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [BAND_URL, axiosPrivate]);

  useEffect(() => {
    const fetchSettingImage = async () => {
      try {
        console.log('Próba pobrania ustawienia z:', SETTING_URL);
        const response = await axiosPrivate.get(SETTING_URL, {
          responseType: 'blob',
          headers: {
            'Accept': 'image/*'
          }
        });
        
        if (response.data.size === 0) {
          console.log('Ustawianie domyślnego obrazka sceny:', DEFAULT_STAGE_IMAGE);
          setSettingPreview(DEFAULT_STAGE_IMAGE);
          return;
        }

        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const imageUrl = URL.createObjectURL(blob);
        setSettingPreview(imageUrl);
      } catch (error) {
        console.error('Błąd podczas pobierania ustawienia:', error);
        if (isAxiosError(error) && error.response) {
          console.error('Status błędu:', error.response.status);
          console.log('Ustawianie domyślnego obrazka sceny po błędzie:', DEFAULT_STAGE_IMAGE);
          setSettingPreview(DEFAULT_STAGE_IMAGE);
        }
      }
    };

    fetchSettingImage();

    return () => {
      if (settingPreview && !settingPreview.includes('scena.png')) {
        URL.revokeObjectURL(settingPreview);
      }
    };
  }, [SETTING_URL, axiosPrivate]);

  useEffect(() => {
    console.log('Aktualny settingPreview:', settingPreview);
  }, [settingPreview]);

  useEffect(() => {
    if (props.data?.position) {
      sessionStorage.setItem('hasPosition', '1');
    } else {
      sessionStorage.setItem('hasPosition', '0');
    }
    console.log("Position: ", sessionStorage.getItem('hasPosition'));
  }, [props.data?.position]);
  
  const handlePositionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setPositionFile(file);
  
    if (file) {
      sessionStorage.setItem('hasPosition', '1');
    } else {
      sessionStorage.setItem('hasPosition', '0');
    }
    console.log("Position1: ", sessionStorage.getItem('hasPosition'));
  };

  const handleSettingUpload = async (file: File) => {
    try {
      const settingFormData = new FormData();
      settingFormData.append('setting', file);
      await axiosPrivate.post(SETTING_UPLOAD_URL, settingFormData);
      
      const imageUrl = URL.createObjectURL(file);
      setSettingPreview(imageUrl);
      setResult(true);
      setShowUploadMessage(true);
    } catch (error) {
      console.error('Błąd podczas wysyłania ustawienia:', error);
      setResult(false);
      setShowUploadMessage(true);
    }
    setOpen(true);
  };

  const handlePictureUpload = async (file: File) => {
    try {
      const bandFormData = new FormData();
      bandFormData.append('band', file);
      await axiosPrivate.post(BAND_UPLOAD_URL, bandFormData);
      
      const imageUrl = URL.createObjectURL(file);
      setImagePreview(imageUrl);
      setResult(true);
      setShowUploadMessage(true);
    } catch (error) {
      console.error('Błąd podczas wysyłania zdjęcia:', error);
      setResult(false);
      setShowUploadMessage(true);
    }
    setOpen(true);
  };

  const handleDownloadScene = async () => {
    try {
      console.log('Rozpoczęcie pobierania obrazka sceny');
      const response = await axiosPrivate.get('/images/scene', {
        responseType: 'blob',
        headers: {
          'Accept': 'image/*'
        }
      });
      
      if (!response.data || response.data.size === 0) {
        console.error('Otrzymano pusty plik');
        return;
      }

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'scena.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Błąd podczas pobierania pustego obrazka sceny:', error);
    }
  };

  const renderSettingImage = () => {
    console.log('Próba wyświetlenia obrazka:', settingPreview);
    if (!settingPreview) return null;
    
    return (
      <img 
        src={settingPreview} 
        alt="Ustawienie na scenie" 
        style={{ 
          maxWidth: '100%', 
          maxHeight: '100%', 
          objectFit: 'contain'
        }} 
        onError={(e) => console.error('Błąd ładowania obrazka:', e)}
      />
    );
  };

  const renderBandImage = () => {
    console.log('Próba wyświetlenia zdjęcia zespołu:', imagePreview);
    if (!imagePreview) return null;
    
    return (
      <img 
        src={imagePreview} 
        alt="Zdjęcie zespołu" 
        style={{ 
          maxWidth: '100%', 
          maxHeight: '100%', 
          objectFit: 'contain'
        }} 
        onError={(e) => console.error('Błąd ładowania zdjęcia zespołu:', e)}
      />
    );
  };

  return (
    <div className="attachment-container">
      <h2>Załączniki</h2>
      <div style={{ marginBottom: '-5px' }}>
        Pusty obrazek sceny do pobrania{' '}
        <span 
          onClick={handleDownloadScene}
          style={{ 
            color: 'blue', 
            textDecoration: 'underline', 
            cursor: 'pointer' 
          }}
        >
          TUTAJ
        </span>
      </div>
      <div className="attachment-content">
        <div className="file-container">
          <label>Ustawienie na scenie</label>
          <input
            type="file"
            accept="image/*"
            id="position"
            onChange={(e) => {
              handlePositionChange(e); 
              if (e.target.files?.[0]) {
                handleSettingUpload(e.target.files[0]);
              }
            }}
            style={{ display: 'none' }}
          />
          <label 
            className="file-input" 
            htmlFor="position"
            data-hover-text={SETTING_HOVER_TEXT}
          >
            {settingPreview ? renderSettingImage() : EMPTY_FILE_BOX_CONTENT}
          </label>
        </div>
        <div className="file-container">
          <label>Zdjęcie zespołu</label>
          <input
            type="file"
            accept="image/*"
            id="picture"
            onChange={(e) => e.target.files?.[0] && handlePictureUpload(e.target.files[0])}
            style={{ display: 'none' }}
          />
          <label 
            className="file-input" 
            htmlFor="picture"
            data-hover-text={PICTURE_HOVER_TEXT}
          >
            {imagePreview ? renderBandImage() : EMPTY_FILE_BOX_CONTENT}
          </label>
        </div>
      </div>
      <MessageBox 
        open={open && showUploadMessage} 
        setOpen={(isOpen) => {
          setOpen(isOpen);
          setShowUploadMessage(isOpen);
        }} 
        error={!result} 
      />
    </div>
  );
};

export default AttachmentSegment;
